import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { Dropdown } from 'react-bootstrap'
import { CalendarSVG } from 'src/components/Icons/Basic'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { useNavigateContext } from 'src/components/Reusable/Navigation/NavigateContext'
import { FilterParam } from '../../FilterBar.types'
import { SimpleDropdownItems } from '../FilterDropdown'
import { DropdownItemType } from '../FilterDropdown.constants'
import { DropDownMenuWithNesting } from '../FilterDropdown.styles'
import { FilterDropdownToggle } from '../../FilterBar.styles'
import { DashboardDateRangeDefaults } from './DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown.types'
import { DashboardDateRangeDefaultsLabel } from './DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown.constants'

// Preset-only date range filter for the Overview Dashboard — no custom range picker.
export const OverviewDashboardDateRangeFilterDropdown = React.memo(
  (): JSX.Element => {
    const location = useLocation()
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const searchParamsFallback = useMemo(
      () => queryString.parse(location.search, { arrayFormat: 'bracket' }),
      [location],
    )
    const {
      navigateState: { parsedQueryParams },
    } = useNavigateContext()
    const searchParams = parsedQueryParams || searchParamsFallback

    const defaultSet = useMemo(
      () => new Set(Array.from(Object.values(DashboardDateRangeDefaults))),
      [],
    )

    const appliedFilters = useMemo((): Record<string, string[]> => {
      const preprocessed = searchParams[FilterParam.DASHBOARD_DATE_RANGE]
      if (!preprocessed) return {}
      const value = Array.isArray(preprocessed) ? preprocessed[0] : preprocessed
      if (value && defaultSet.has(value as DashboardDateRangeDefaults)) {
        return { [FilterParam.DASHBOARD_DATE_RANGE]: [value] }
      }
      return {}
    }, [searchParams, defaultSet])

    useEffect(() => {
      setDropdownOpen(false)
    }, [location.search])

    const filterValues: DropdownItemType[] = useMemo(
      () => [
        { value: DashboardDateRangeDefaults.LAST_7_DAYS, label: 'Last 7 Days' },
        {
          value: DashboardDateRangeDefaults.LAST_30_DAYS,
          label: 'Last 30 Days',
        },
        {
          value: DashboardDateRangeDefaults.LAST_90_DAYS,
          label: 'Last 90 Days',
        },
        { value: DashboardDateRangeDefaults.LAST_YEAR, label: 'Last Year' },
      ],
      [],
    )

    const toggleText =
      DashboardDateRangeDefaultsLabel[
        appliedFilters[
          FilterParam.DASHBOARD_DATE_RANGE
        ]?.[0] as DashboardDateRangeDefaults
      ] ??
      DashboardDateRangeDefaultsLabel[DashboardDateRangeDefaults.LAST_7_DAYS]

    return (
      <Dropdown show={dropdownOpen} onToggle={setDropdownOpen}>
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          id="overview-date-range-filter"
        >
          <FlexRow align={FlexAlign.CENTER} gap={10}>
            <CalendarSVG width={16} height={16} />
            {toggleText}
          </FlexRow>
        </Dropdown.Toggle>
        <DropDownMenuWithNesting>
          <SimpleDropdownItems
            filterParam={FilterParam.DASHBOARD_DATE_RANGE}
            filterValues={filterValues}
            location={location}
            searchParams={searchParams}
            appliedFilters={appliedFilters}
            replaceParam
          />
        </DropDownMenuWithNesting>
      </Dropdown>
    )
  },
)
